import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, SearchInput, SegmentedControl, Switch } from '../../../components/ui';
import { activeFilterCount, DEFAULT_FILTERS, InfluenceFilters, InfluenceModel, searchEntities } from './influenceModel';
import { areaColor, EntityAvatar } from './ui';

/* ---------------- Filters ---------------- */

export const FiltersPanel: React.FC<{
  model: InfluenceModel;
  filters: InfluenceFilters;
  onChange: (filters: InfluenceFilters) => void;
}> = ({ model, filters, onChange }) => {
  const counts = useMemo(() => {
    const byArea = new Map<string, number>();
    let people = 0;
    let orgs = 0;
    model.entities.forEach((e) => {
      if (e.type === 'stakeholder') people++;
      else orgs++;
      e.scores.forEach((s) => byArea.set(s.categoryId, (byArea.get(s.categoryId) || 0) + 1));
    });
    return { byArea, people, orgs, unassessed: model.entities.filter((e) => !e.assessed).length };
  }, [model]);
  const enabled = new Set(filters.areaIds || model.areas.map((a) => a.id));
  const toggleArea = (id: string) => {
    const next = new Set(enabled);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const ids = model.areas.map((a) => a.id).filter((a) => next.has(a));
    onChange({ ...filters, areaIds: ids.length === model.areas.length ? null : ids });
  };

  return (
    <div className="space-y-5">
      <section>
        <p className="mb-1.5 text-[12px] text-neutral-500">Type</p>
        <SegmentedControl
          label="Entity type"
          value={filters.type}
          onChange={(type) => onChange({ ...filters, type })}
          className="w-full [&>button]:flex-1 [&>button]:justify-center [&>button]:px-2"
          items={[
            { value: 'all', label: <>All <span className="tabular-nums text-neutral-400">{model.entities.length}</span></> },
            { value: 'stakeholder', label: <>People <span className="tabular-nums text-neutral-400">{counts.people}</span></> },
            { value: 'organization', label: <>Orgs <span className="tabular-nums text-neutral-400">{counts.orgs}</span></> },
          ]}
        />
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <p className="text-[12px] text-neutral-500">Areas</p>
          <span className="flex items-center gap-0.5">
            <Button size="xs" variant="ghost" className="!h-6 !px-2" onClick={() => onChange({ ...filters, areaIds: null })}>
              All
            </Button>
            <Button size="xs" variant="ghost" className="!h-6 !px-2" onClick={() => onChange({ ...filters, areaIds: [] })}>
              None
            </Button>
          </span>
        </div>
        <div className="-mx-2">
          {model.areas.map((area) => (
            <Checkbox
              key={area.id}
              className="flex h-10 w-full items-center gap-2 rounded-lg px-2 text-left transition-colors hover:bg-neutral-50 sm:h-8"
              checked={enabled.has(area.id)}
              onChange={() => toggleArea(area.id)}
              label={
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(area.color) }} />
                  <span className="truncate">{area.label}</span>
                  <span className="ml-auto text-[12px] tabular-nums text-neutral-400">{counts.byArea.get(area.id) || 0}</span>
                </span>
              }
            />
          ))}
        </div>
      </section>

      <section>
        <label className="block">
          <span className="mb-1.5 flex items-center justify-between text-[12px] text-neutral-500">
            Min score <span className="tabular-nums text-ink">{filters.minScore}</span>
          </span>
          <input
            type="range"
            min={0}
            max={95}
            step={5}
            value={filters.minScore}
            onChange={(e) => onChange({ ...filters, minScore: Number(e.target.value) })}
            className="h-1 w-full cursor-pointer accent-ink"
            aria-label="Minimum score"
          />
        </label>
      </section>

      <section className="flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="block text-[13px] text-ink">Unassessed</span>
          <span className="block text-[12px] text-neutral-400">{counts.unassessed} without scores</span>
        </span>
        <Switch label="Show unassessed" checked={filters.showUnassessed} onChange={(showUnassessed) => onChange({ ...filters, showUnassessed })} />
      </section>

      {activeFilterCount(filters) > 0 && (
        <Button size="sm" variant="ghost" className="-ml-3" onClick={() => onChange(DEFAULT_FILTERS)}>
          Reset filters
        </Button>
      )}
    </div>
  );
};

/* ---------------- Search ---------------- */

export const EntitySearch: React.FC<{
  model: InfluenceModel;
  value: string;
  onChange: (value: string) => void;
  onPick: (id: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  className?: string;
  autoFocus?: boolean;
  onDone?: () => void;
}> = ({ model, value, onChange, onPick, inputRef, className = '', autoFocus, onDone }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const results = useMemo(() => searchEntities(model.entities, value, 8), [model.entities, value]);

  useEffect(() => setActive(0), [value]);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const pick = (id: string) => {
    onPick(id);
    onChange('');
    setOpen(false);
    inputRef?.current?.blur();
    onDone?.();
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <SearchInput
        ref={inputRef}
        value={value}
        autoFocus={autoFocus}
        placeholder="Search"
        aria-label="Search entities"
        role="combobox"
        aria-expanded={open && !!value.trim()}
        aria-controls={listId}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(results.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter' && results[active]) {
            e.preventDefault();
            pick(results[active].id);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            if (value) onChange('');
            setOpen(false);
            (e.target as HTMLInputElement).blur();
            onDone?.();
          }
        }}
      />
      {open && value.trim() && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-[60vh] min-w-[260px] overflow-y-auto rounded-2xl bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06] infl-rise"
        >
          {results.length === 0 && <li className="px-3 py-2 text-[13px] text-neutral-400">No matches</li>}
          {results.map((e, i) => (
            <li key={e.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(e.id)}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left ${i === active ? 'bg-neutral-100' : ''}`}
              >
                <EntityAvatar entity={e} size="xs" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-ink">{e.label}</span>
                  <span className="block truncate text-[11.5px] text-neutral-400">{e.subtitle}</span>
                </span>
                {e.dominant ? (
                  <span className="flex items-center gap-1.5 text-[12px] tabular-nums text-neutral-500">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(e.dominant.color) }} />
                    {e.average}
                  </span>
                ) : (
                  <span className="text-[11px] text-neutral-300">—</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
